import React, { useState, useRef } from "react";
import { api, imageUrl, formatApiError } from "@/lib/api";

/* Reusable bilingual text field (FI + EN side-by-side) */
const BilingualField = ({
  label,
  fiValue,
  enValue,
  onChangeFi,
  onChangeEn,
  type = "input",
  fiPlaceholder = "",
  enPlaceholder = "",
  testidBase,
}) => {
  const Cmp = type === "textarea" ? "textarea" : "input";
  const props = {
    className: "w-full px-3 py-2 rounded-xl border outline-none text-[14px] bg-white",
    style: { borderColor: "#DCE3ED", color: "var(--navy)" },
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {label && (
        <div
          className="md:col-span-2 text-xs font-bold"
          style={{
            color: "var(--navy)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}
      <div>
        <div
          className="text-[10px] mb-1"
          style={{
            color: "var(--mute)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontFamily: "var(--font-mono)",
          }}
        >
          🇫🇮 Finnois
        </div>
        <Cmp
          {...props}
          rows={type === "textarea" ? 3 : undefined}
          value={fiValue || ""}
          placeholder={fiPlaceholder}
          onChange={(e) => onChangeFi(e.target.value)}
          data-testid={testidBase ? `${testidBase}-fi` : undefined}
        />
      </div>
      <div>
        <div
          className="text-[10px] mb-1"
          style={{
            color: "var(--mute)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontFamily: "var(--font-mono)",
          }}
        >
          🇬🇧 Anglais
        </div>
        <Cmp
          {...props}
          rows={type === "textarea" ? 3 : undefined}
          value={enValue || ""}
          placeholder={enPlaceholder}
          onChange={(e) => onChangeEn(e.target.value)}
          data-testid={testidBase ? `${testidBase}-en` : undefined}
        />
      </div>
    </div>
  );
};

/* FAQ + Why share the same shape: list of {q/t: {fi,en}, a/d: {fi,en}}.
   This generic component handles add/remove/edit/reorder. */
const BilingualList = ({
  items,
  onChange,
  fieldA,
  fieldB,
  labelA,
  labelB,
  emptyLabel,
  testidBase,
  preset = null,
}) => {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const setSub = (i, field, lang, value) => {
    const cur = items[i] || {};
    const subPrev = (cur && cur[field]) || { fi: "", en: "" };
    update(i, { [field]: { ...subPrev, [lang]: value } });
  };
  const addItem = () => {
    onChange([
      ...items,
      {
        [fieldA]: { fi: "", en: "" },
        [fieldB]: { fi: "", en: "" },
      },
    ]);
  };
  const removeItem = (i) => {
    onChange(items.filter((_, idx) => idx !== i));
  };
  const moveItem = (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const copy = [...items];
    [copy[i], copy[target]] = [copy[target], copy[i]];
    onChange(copy);
  };

  return (
    <div className="space-y-4">
      {items.length === 0 && (
        <div
          className="text-sm rounded-xl p-4 text-center"
          style={{ background: "var(--cream-2)", color: "var(--mute)" }}
        >
          {emptyLabel}
        </div>
      )}
      {items.map((it, i) => (
        <div
          key={i}
          className="rounded-2xl p-4"
          style={{ background: "var(--cream-2)" }}
          data-testid={`${testidBase}-item-${i}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="text-xs font-bold"
              style={{
                color: "var(--navy)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              #{i + 1}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => moveItem(i, -1)}
                disabled={i === 0}
                className="px-2 py-1 rounded-lg text-xs"
                style={{
                  background: "#FFFFFF",
                  color: "var(--navy)",
                  opacity: i === 0 ? 0.3 : 1,
                  border: "1px solid #DCE3ED",
                }}
                aria-label="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => moveItem(i, 1)}
                disabled={i === items.length - 1}
                className="px-2 py-1 rounded-lg text-xs"
                style={{
                  background: "#FFFFFF",
                  color: "var(--navy)",
                  opacity: i === items.length - 1 ? 0.3 : 1,
                  border: "1px solid #DCE3ED",
                }}
                aria-label="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="px-3 py-1 rounded-lg text-xs font-bold"
                style={{ background: "#FFE9EA", color: "#B3263F" }}
                data-testid={`${testidBase}-remove-${i}`}
              >
                Supprimer
              </button>
            </div>
          </div>
          <div className="space-y-3">
            <BilingualField
              label={labelA}
              fiValue={it[fieldA]?.fi}
              enValue={it[fieldA]?.en}
              onChangeFi={(v) => setSub(i, fieldA, "fi", v)}
              onChangeEn={(v) => setSub(i, fieldA, "en", v)}
              testidBase={`${testidBase}-${i}-${fieldA}`}
            />
            <BilingualField
              label={labelB}
              fiValue={it[fieldB]?.fi}
              enValue={it[fieldB]?.en}
              onChangeFi={(v) => setSub(i, fieldB, "fi", v)}
              onChangeEn={(v) => setSub(i, fieldB, "en", v)}
              type="textarea"
              testidBase={`${testidBase}-${i}-${fieldB}`}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="w-full rounded-2xl py-3 font-bold transition-colors"
        style={{
          background: "var(--orange-soft)",
          color: "var(--orange-2)",
          border: "2px dashed var(--orange)",
        }}
        data-testid={`${testidBase}-add`}
      >
        ＋ Ajouter
      </button>
      {preset && items.length === 0 && (
        <button
          type="button"
          onClick={() => onChange(preset)}
          className="w-full rounded-2xl px-4 py-3 font-bold text-sm"
          style={{
            background: "#FFFFFF",
            border: "1px solid #DCE3ED",
            color: "var(--navy)",
          }}
          data-testid={`${testidBase}-load-preset`}
        >
          Charger les valeurs par défaut pour les personnaliser
        </button>
      )}
    </div>
  );
};

/* Hero image upload */
const HeroImageUploader = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const data = await api.uploadImage(file);
      onChange(data.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const preview = value ? imageUrl(value) : "/images/anypaku-van.png";

  return (
    <div className="rounded-2xl p-5" style={{ background: "var(--cream-2)" }}>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-center">
        <div
          className="rounded-xl overflow-hidden flex items-center justify-center"
          style={{
            background: "#FFFFFF",
            border: "1px solid #DCE3ED",
            minHeight: 120,
          }}
        >
          <img
            src={preview}
            alt="Aperçu van"
            style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain" }}
            data-testid="hero-image-preview"
          />
        </div>
        <div className="space-y-3">
          <div className="text-sm" style={{ color: "var(--mute)", lineHeight: 1.5 }}>
            Téléchargez la photo de votre fourgon (PNG, JPG ou WebP, max 8 Mo). Idéalement avec un fond transparent.
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="hero-image-input"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              style={{ padding: "10px 18px", fontSize: 14 }}
              data-testid="hero-image-upload-btn"
            >
              {uploading ? "Envoi…" : "📤 Choisir une photo"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="btn-ghost"
                style={{ padding: "10px 18px", fontSize: 14 }}
                data-testid="hero-image-reset"
              >
                ↺ Revenir à la photo par défaut
              </button>
            )}
          </div>
          {error && (
            <div className="text-xs" style={{ color: "#B3263F" }}>
              ⚠️ {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* Colors picker */
const ColorPicker = ({ value, onChange }) => {
  const colors = value || { navy: "#1A4B8C", orange: "#F58220" };
  const set = (k, v) => onChange({ ...colors, [k]: v });
  const reset = () => onChange(null);
  return (
    <div className="rounded-2xl p-5" style={{ background: "var(--cream-2)" }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { key: "navy", label: "Bleu (titres, boutons primaires)" },
          { key: "orange", label: "Orange (accents, CTA)" },
        ].map(({ key, label }) => (
          <div key={key}>
            <div
              className="text-xs font-bold mb-2"
              style={{
                color: "var(--navy)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={colors[key]}
                onChange={(e) => set(key, e.target.value)}
                className="rounded-lg cursor-pointer"
                style={{ width: 56, height: 40, border: "1px solid #DCE3ED" }}
                data-testid={`color-${key}-picker`}
              />
              <input
                type="text"
                value={colors[key]}
                onChange={(e) => set(key, e.target.value)}
                className="px-3 py-2 rounded-xl border outline-none text-[13px] font-mono"
                style={{
                  borderColor: "#DCE3ED",
                  color: "var(--navy)",
                  background: "#FFFFFF",
                  width: 120,
                }}
                data-testid={`color-${key}-hex`}
              />
            </div>
          </div>
        ))}
      </div>
      {value && (
        <button
          type="button"
          onClick={reset}
          className="mt-3 text-xs"
          style={{ color: "var(--orange-2)" }}
          data-testid="colors-reset"
        >
          ↺ Restaurer les couleurs par défaut
        </button>
      )}
    </div>
  );
};

/* Trust badges builder: icon (emoji or text) + number/label + bilingual subtitle */
const DEFAULT_TRUST_PRESET = [
  { icon: "★", num: "4.9/5", label: { fi: "240+ arviota", en: "240+ reviews" } },
  { icon: "⚡", num: "< 60 min", label: { fi: "vasteaika", en: "response time" } },
  { icon: "🛡", num: "5 000 €", label: { fi: "vakuutettu", en: "insured" } },
];

const DEFAULT_WHY_PRESET = [
  {
    t: { fi: "Paikalla tunnissa", en: "On site within the hour" },
    d: {
      fi: "Helsingin sisällä keskimääräinen vasteaika alle 60 min.",
      en: "Average response time under 60 min within Helsinki.",
    },
  },
  {
    t: { fi: "Kiinteä hinta", en: "Fixed price" },
    d: {
      fi: "Hinta lyödään lukkoon ennen lähtöä. Ei piilokuluja.",
      en: "Price locked before departure. No hidden fees.",
    },
  },
  {
    t: { fi: "Vakuutettu", en: "Insured" },
    d: {
      fi: "Esineesi vakuutettu kuljetuksen ajan 5 000 €:oon asti.",
      en: "Your items insured up to €5,000 during transport.",
    },
  },
  {
    t: { fi: "Suomeksi & englanniksi", en: "Finnish & English" },
    d: {
      fi: "Kuljettajat puhuvat sujuvasti molempia kieliä.",
      en: "Our drivers speak both fluently.",
    },
  },
];

const DEFAULT_FAQ_PRESET = [
  {
    q: {
      fi: "Kuinka nopeasti pääsette paikalle?",
      en: "How quickly can you arrive?",
    },
    a: {
      fi: "Helsingin alueella keskimäärin 45–60 minuutin sisällä tilauksesta. Voit myös varata ajan etukäteen.",
      en: "Within Helsinki, on average 45–60 minutes after booking. You can also schedule in advance.",
    },
  },
  {
    q: { fi: "Mitä maksutapoja hyväksytte?", en: "Which payment methods?" },
    a: {
      fi: "MobilePay, käteinen, kaikki yleiset pankkikortit. Yritysasiakkaille laskutus.",
      en: "MobilePay, cash, all major bank cards. Invoicing for businesses.",
    },
  },
  {
    q: { fi: "Mikä on auton tilavuus?", en: "What's the van capacity?" },
    a: {
      fi: "Pakettiautomme tilavuus on 11 m³, ihanteellinen yksiön tai kaksion muuttoon.",
      en: "11 m³ — ideal for studios and one-bedroom flats.",
    },
  },
  {
    q: { fi: "Voinko peruuttaa varauksen?", en: "Can I cancel my booking?" },
    a: {
      fi: "Kyllä, 2 tuntia ennen sovittua aikaa ilmaiseksi.",
      en: "Yes, free cancellation up to 2 hours before the scheduled time.",
    },
  },
];

const TrustBadgesEditor = ({ items, onChange }) => {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const setLabel = (i, lang, value) => {
    const cur = items[i] || {};
    const prev = cur.label || { fi: "", en: "" };
    update(i, { label: { ...prev, [lang]: value } });
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= items.length) return;
    const c = [...items];
    [c[i], c[t]] = [c[t], c[i]];
    onChange(c);
  };
  const add = () =>
    onChange([...items, { icon: "★", num: "", label: { fi: "", en: "" } }]);
  const loadPreset = () => onChange(DEFAULT_TRUST_PRESET);

  return (
    <div className="space-y-4">
      {items.length === 0 && (
        <div
          className="text-sm rounded-xl p-4 text-center"
          style={{ background: "var(--cream-2)", color: "var(--mute)" }}
        >
          Aucun badge personnalisé — les 3 badges par défaut s'affichent
          (4.9/5 · &lt; 60 min · 5 000 €).
        </div>
      )}
      {items.map((it, i) => (
        <div
          key={i}
          className="rounded-2xl p-4"
          style={{ background: "var(--cream-2)" }}
          data-testid={`trust-item-${i}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="text-xs font-bold"
              style={{
                color: "var(--navy)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Badge #{i + 1}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="px-2 py-1 rounded-lg text-xs"
                style={{
                  background: "#FFFFFF",
                  color: "var(--navy)",
                  opacity: i === 0 ? 0.3 : 1,
                  border: "1px solid #DCE3ED",
                }}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                className="px-2 py-1 rounded-lg text-xs"
                style={{
                  background: "#FFFFFF",
                  color: "var(--navy)",
                  opacity: i === items.length - 1 ? 0.3 : 1,
                  border: "1px solid #DCE3ED",
                }}
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                className="px-3 py-1 rounded-lg text-xs font-bold"
                style={{ background: "#FFE9EA", color: "#B3263F" }}
                data-testid={`trust-remove-${i}`}
              >
                Supprimer
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <div
                className="text-[10px] mb-1"
                style={{
                  color: "var(--mute)",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontFamily: "var(--font-mono)",
                }}
              >
                Icône
              </div>
              <input
                type="text"
                value={it.icon || ""}
                onChange={(e) => update(i, { icon: e.target.value })}
                maxLength={4}
                placeholder="★"
                className="w-full px-3 py-2 rounded-xl border outline-none text-center text-lg"
                style={{
                  borderColor: "#DCE3ED",
                  color: "var(--orange)",
                  background: "#FFFFFF",
                }}
                data-testid={`trust-${i}-icon`}
              />
            </div>
            <div className="sm:col-span-3">
              <div
                className="text-[10px] mb-1"
                style={{
                  color: "var(--mute)",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontFamily: "var(--font-mono)",
                }}
              >
                Chiffre / Valeur en gras
              </div>
              <input
                type="text"
                value={it.num || ""}
                onChange={(e) => update(i, { num: e.target.value })}
                placeholder="ex. 4.9/5"
                className="w-full px-3 py-2 rounded-xl border outline-none text-[14px]"
                style={{
                  borderColor: "#DCE3ED",
                  color: "var(--navy)",
                  background: "#FFFFFF",
                }}
                data-testid={`trust-${i}-num`}
              />
            </div>
          </div>
          <BilingualField
            label="Petit texte sous la valeur"
            fiValue={it.label?.fi}
            enValue={it.label?.en}
            onChangeFi={(v) => setLabel(i, "fi", v)}
            onChangeEn={(v) => setLabel(i, "en", v)}
            fiPlaceholder="vakuutettu"
            enPlaceholder="insured"
            testidBase={`trust-${i}-label`}
          />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={add}
          className="flex-1 rounded-2xl py-3 font-bold transition-colors"
          style={{
            background: "var(--orange-soft)",
            color: "var(--orange-2)",
            border: "2px dashed var(--orange)",
          }}
          data-testid="trust-add"
        >
          ＋ Ajouter un badge
        </button>
        {items.length === 0 && (
          <button
            type="button"
            onClick={loadPreset}
            className="rounded-2xl px-4 py-3 font-bold text-sm"
            style={{ background: "#FFFFFF", border: "1px solid #DCE3ED", color: "var(--navy)" }}
            data-testid="trust-load-preset"
          >
            Charger les 3 défauts pour personnaliser
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------- MAIN BUILDER ---------- */
export default function AdminBuilder({ config, onChange }) {
  const setFaq = (faqItems) => onChange({ ...config, faqItems });
  const setWhy = (whyItems) => onChange({ ...config, whyItems });
  const setTrust = (trustBadges) => onChange({ ...config, trustBadges });
  const setHeroImage = (heroImage) => onChange({ ...config, heroImage });
  const setColors = (colors) => onChange({ ...config, colors });

  const [activeTab, setActiveTab] = useState("faq");
  const tabs = [
    { id: "faq", label: "❓ FAQ" },
    { id: "why", label: "⭐ Pourquoi Anypaku" },
    { id: "trust", label: "🛡 Badges hero" },
    { id: "image", label: "🖼️ Photo du van" },
    { id: "colors", label: "🎨 Couleurs" },
  ];

  return (
    <section
      className="rounded-3xl p-6 md:p-8"
      style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
      data-testid="admin-builder-section"
    >
      <h2 className="display mb-2" style={{ fontSize: 26, color: "var(--navy)" }}>
        🧰 Builder du site
      </h2>
      <p className="text-sm mb-5" style={{ color: "var(--mute)" }}>
        Ajoute, supprime ou réorganise les éléments dynamiques du site. N'oublie
        pas d'<strong>enregistrer</strong> en bas pour publier.
      </p>

      <div
        className="flex flex-wrap gap-2 mb-5 pb-4"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className="px-3 py-2 rounded-xl text-sm font-bold transition-colors"
              style={{
                background: active ? "var(--navy)" : "var(--cream-2)",
                color: active ? "#FFFFFF" : "var(--navy)",
                border: active ? "1px solid var(--navy)" : "1px solid transparent",
              }}
              data-testid={`builder-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "faq" && (
        <div data-testid="builder-panel-faq">
          <p className="text-xs mb-3" style={{ color: "var(--mute)" }}>
            Si la liste est vide, ce sont les <strong>4 questions par défaut</strong> qui s'affichent. Dès que vous ajoutez une question ici, elles remplacent les questions par défaut.
          </p>
          <BilingualList
            items={config.faqItems || []}
            onChange={setFaq}
            fieldA="q"
            fieldB="a"
            labelA="Question"
            labelB="Réponse"
            emptyLabel="Aucune question ajoutée — les 4 questions par défaut sont affichées."
            testidBase="faq"
            preset={DEFAULT_FAQ_PRESET}
          />
        </div>
      )}

      {activeTab === "why" && (
        <div data-testid="builder-panel-why">
          <p className="text-xs mb-3" style={{ color: "var(--mute)" }}>
            Les 4 cartes "Pourquoi Anypaku". Vide = valeurs par défaut. Cliquez sur <strong>"Charger les valeurs par défaut"</strong> pour les modifier ou en supprimer.
          </p>
          <BilingualList
            items={config.whyItems || []}
            onChange={setWhy}
            fieldA="t"
            fieldB="d"
            labelA="Titre du point"
            labelB="Description courte"
            emptyLabel="Aucun atout personnalisé — les 4 points par défaut sont affichés."
            testidBase="why"
            preset={DEFAULT_WHY_PRESET}
          />
        </div>
      )}

      {activeTab === "trust" && (
        <div data-testid="builder-panel-trust">
          <p className="text-xs mb-3" style={{ color: "var(--mute)" }}>
            Les 3 badges en haut du site (sous les boutons Appeler / WhatsApp).
            Cliquez sur "Charger les 3 défauts" pour les personnaliser, ou ajoutez
            les vôtres. Liste vide = les 3 badges par défaut s'affichent.
          </p>
          <TrustBadgesEditor
            items={config.trustBadges || []}
            onChange={setTrust}
          />
        </div>
      )}

      {activeTab === "image" && (
        <div data-testid="builder-panel-image">
          <HeroImageUploader value={config.heroImage} onChange={setHeroImage} />
        </div>
      )}

      {activeTab === "colors" && (
        <div data-testid="builder-panel-colors">
          <ColorPicker value={config.colors} onChange={setColors} />
        </div>
      )}
    </section>
  );
}
